import { Controller } from '@hotwired/stimulus'

/**
 * Enhanced Form Controller
 * Provides improved UX features for the booking contact form:
 * - Real-time validation feedback
 * - Character counters
 * - Progress tracking
 * - Accessibility enhancements
 * - Smart form submission
 */
export default class extends Controller {
  static targets = [
    'form',
    'requiredField',
    'emailField',
    'phoneField',
    'notesField',
    'notesCounter',
    'submitButton'
  ]

  connect() {
    this.initializeFormEnhancements()
    this.setupValidation()
    this.setupCharacterCounters()
    this.setupProgressTracking()
  }

  initializeFormEnhancements() {
    // Add form submission protection
    this.formTarget.addEventListener('submit', this.handleSubmit.bind(this))

    // Setup autosave for draft functionality (optional)
    this.setupAutosave()

    // Add keyboard navigation enhancements
    this.setupKeyboardNavigation()
  }

  setupValidation() {
    // Real-time validation for required fields
    this.requiredFieldTargets.forEach(field => {
      field.addEventListener('blur', this.validateRequiredField.bind(this))
      field.addEventListener('input', this.clearFieldError.bind(this))
    })

    // Email validation
    if (this.hasEmailFieldTarget) {
      this.emailFieldTarget.addEventListener('blur', this.validateEmail.bind(this))
    }

    // Phone validation
    if (this.hasPhoneFieldTarget) {
      this.phoneFieldTarget.addEventListener('input', this.formatPhoneNumber.bind(this))
      this.phoneFieldTarget.addEventListener('blur', this.validatePhone.bind(this))
    }
  }

  setupCharacterCounters() {
    if (this.hasNotesFieldTarget && this.hasNotesCounterTarget) {
      this.notesFieldTarget.addEventListener('input', this.updateNotesCounter.bind(this))
      // Initialize counter
      this.updateNotesCounter()
    }
  }

  setupProgressTracking() {
    // Track form completion progress
    const allFields = this.formTarget.querySelectorAll('input[required], select[required], textarea[required]')
    allFields.forEach(field => {
      field.addEventListener('blur', this.updateProgress.bind(this))
    })
  }

  // Validation methods
  validateRequiredField(event) {
    const field = event.target
    const value = field.value.trim()

    if (!value) {
      this.showFieldError(field, 'Ce champ est obligatoire')
    } else {
      this.clearFieldError(event)
    }
  }

  validateEmail(event) {
    const field = event.target
    const email = field.value.trim()

    if (email && !this.isValidEmail(email)) {
      this.showFieldError(field, 'Veuillez entrer une adresse email valide')
    }
  }

  validatePhone(event) {
    const field = event.target
    const phone = field.value.trim()

    if (phone && !this.isValidPhone(phone)) {
      this.showFieldError(field, 'Veuillez entrer un numéro de téléphone valide')
    }
  }

  // Helper methods
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  isValidPhone(phone) {
    // Basic phone validation - allows international formats
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{8,}$/
    return phoneRegex.test(phone.replace(/\s/g, ''))
  }

  formatPhoneNumber(event) {
    const field = event.target
    let value = field.value.replace(/\D/g, '')

    // Simple French phone formatting
    if (value.startsWith('33')) {
      value = '+33 ' + value.slice(2).replace(/(\d{1})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5')
    } else if (value.length === 10) {
      value = value.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5')
    }

    field.value = value
  }

  showFieldError(field, message) {
    // Remove existing error
    this.clearFieldError({ target: field })

    // Add error styling
    field.classList.add('border-red-500', 'ring-red-200')
    field.classList.remove('border-green-300', 'ring-green-100')

    // Add error message
    const errorDiv = document.createElement('div')
    errorDiv.className = 'mt-1 text-sm text-red-600 field-error'
    errorDiv.textContent = message
    errorDiv.setAttribute('role', 'alert')

    // Insert after field
    field.parentNode.appendChild(errorDiv)

    // Add ARIA attributes
    const errorId = `error-${Date.now()}`
    errorDiv.id = errorId
    field.setAttribute('aria-describedby', errorId)
    field.setAttribute('aria-invalid', 'true')
  }

  clearFieldError(event) {
    const field = event.target

    // Remove error styling
    field.classList.remove('border-red-500', 'ring-red-200')

    // Remove error message
    const errorDiv = field.parentNode.querySelector('.field-error')
    if (errorDiv) {
      errorDiv.remove()
    }

    // Remove ARIA attributes
    field.removeAttribute('aria-describedby')
    field.removeAttribute('aria-invalid')

    // Add success styling if field has value
    if (field.value.trim()) {
      field.classList.add('border-green-300', 'ring-green-100')
    } else {
      field.classList.remove('border-green-300', 'ring-green-100')
    }
  }

  updateNotesCounter() {
    const currentLength = this.notesFieldTarget.value.length
    const maxLength = this.notesFieldTarget.getAttribute('maxlength') || 500

    this.notesCounterTarget.textContent = `${currentLength}/${maxLength} caractères`

    // Color coding for character count
    const percentage = currentLength / maxLength
    if (percentage > 0.9) {
      this.notesCounterTarget.className = 'text-xs text-red-500'
    } else if (percentage > 0.7) {
      this.notesCounterTarget.className = 'text-xs text-orange-500'
    } else {
      this.notesCounterTarget.className = 'text-xs text-gray-400'
    }
  }

  updateProgress() {
    // Calculate form completion percentage
    const requiredFields = this.formTarget.querySelectorAll('input[required], select[required], textarea[required]')
    const completedFields = Array.from(requiredFields).filter(field => field.value.trim() !== '')

    const progress = Math.round((completedFields.length / requiredFields.length) * 100)

    // Update progress bar if it exists
    const progressBar = this.formTarget.querySelector('.bg-blue-600.h-2')
    if (progressBar) {
      progressBar.style.width = `${Math.max(33, progress)}%`
    }

    // Update progress indicators
    const indicators = this.formTarget.querySelectorAll('.w-2.h-2.bg-gray-300')
    indicators.forEach((indicator, index) => {
      if (index < Math.ceil(progress / 33)) {
        indicator.classList.remove('bg-gray-300')
        indicator.classList.add('bg-blue-600')
      }
    })
  }

  handleSubmit(event) {
    // Prevent double submission
    if (this.isSubmitting) {
      event.preventDefault()
      return
    }

    this.isSubmitting = true

    // Show loading state
    if (this.hasSubmitButtonTarget) {
      const originalText = this.submitButtonTarget.innerHTML
      this.submitButtonTarget.innerHTML = `
        <svg class="mr-3 -ml-1 w-5 h-5 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Enregistrement en cours...
      `
      this.submitButtonTarget.disabled = true
    }

    // Validate form before submission
    const isValid = this.validateForm()
    if (!isValid) {
      event.preventDefault()
      this.resetSubmitButton()
      return
    }

    // Form is valid, allow submission
    // Reset will happen on page reload/redirect
  }

  validateForm() {
    let isValid = true

    // Validate all required fields
    this.requiredFieldTargets.forEach(field => {
      if (!field.value.trim()) {
        this.showFieldError(field, 'Ce champ est obligatoire')
        isValid = false
      }
    })

    // Validate email if present
    if (this.hasEmailFieldTarget && this.emailFieldTarget.value.trim()) {
      if (!this.isValidEmail(this.emailFieldTarget.value.trim())) {
        this.showFieldError(this.emailFieldTarget, 'Veuillez entrer une adresse email valide')
        isValid = false
      }
    }

    // Focus first error field
    if (!isValid) {
      const firstError = this.formTarget.querySelector('.border-red-500')
      if (firstError) {
        firstError.focus()
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }

    return isValid
  }

  resetSubmitButton() {
    this.isSubmitting = false
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      const text = this.formTarget.querySelector('[data-booking-contact-persisted]') ?
        'Mettre à jour le contact' : 'Créer le contact'
      this.submitButtonTarget.innerHTML = `
        <svg class="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        ${text}
      `
    }
  }

  setupAutosave() {
    // Optional: Save form data to localStorage for recovery
    let autosaveTimeout

    const saveFormData = () => {
      const formData = new FormData(this.formTarget)
      const data = {}

      for (let [key, value] of formData.entries()) {
        if (key.includes('[') && key.includes(']')) {
          // Skip nested venue data for autosave
          continue
        }
        data[key] = value
      }

      localStorage.setItem('booking_contact_draft', JSON.stringify(data))
    }

    this.formTarget.addEventListener('input', () => {
      clearTimeout(autosaveTimeout)
      autosaveTimeout = setTimeout(saveFormData, 2000) // Save after 2 seconds of inactivity
    })
  }

  setupKeyboardNavigation() {
    // Enhanced keyboard navigation
    this.formTarget.addEventListener('keydown', (event) => {
      // Quick save with Ctrl+S (or Cmd+S on Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault()
        this.formTarget.requestSubmit()
      }

      // Navigate between sections with Ctrl+Arrow keys
      if (event.ctrlKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        event.preventDefault()
        this.navigateSections(event.key === 'ArrowDown')
      }
    })
  }

  navigateSections(down = true) {
    const sections = this.formTarget.querySelectorAll('.bg-white.rounded-xl')
    const currentSection = document.activeElement.closest('.bg-white.rounded-xl')

    if (!currentSection) return

    const currentIndex = Array.from(sections).indexOf(currentSection)
    const nextIndex = down ?
      Math.min(currentIndex + 1, sections.length - 1) :
      Math.max(currentIndex - 1, 0)

    const nextSection = sections[nextIndex]
    const firstInput = nextSection.querySelector('input, select, textarea')

    if (firstInput) {
      firstInput.focus()
      nextSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  disconnect() {
    // Cleanup autosave data when leaving the form
    localStorage.removeItem('booking_contact_draft')
  }
}
